import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MaterialReactTable } from 'material-react-table';
import { 
  FiSearch, 
  FiPlus, 
  FiX,
  FiCheck,
  FiAlertTriangle,
  FiEye,
  FiFilter,
  FiInfo,
  FiUser,
  FiChevronDown,
  FiDollarSign,
  FiCreditCard,
  FiFileText,
  FiClock,
  FiCheckCircle,
  FiXCircle
} from 'react-icons/fi';
import { PatientBillingService, BillingUtils } from '../../../../BillableActivities/Services/billingService';
import TopHeader from "../../../../common/Header/TopHeader";
import PatientMedicalClinicalData from '../../../../Patient/listPrincipalComponent/TableComponent/PatientMedicalClinicalData';
import PatientManagement from '../../../../common/Menu/PatientManagement';
import NewOrderTreatment from './NewOrderTreatment';

const OrdersTreatment = ({ onCountUpdate, componentName = 'OrdersTreatment' }) => {
  const location = useLocation();
  
  // Obtener datos del paciente desde navigation state
  const patientData = location.state;
  const patientId = patientData?.patientId;

const [showNewOrderModal, setShowNewOrderModal] = useState(false);
  const [activities, setActivities] = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rowSelection, setRowSelection] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [alertInfo, setAlertInfo] = useState(null);
  const [showFilterOffcanvas, setShowFilterOffcanvas] = useState(false);
  const [showViewOffcanvas, setShowViewOffcanvas] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState(false);
  const [showStatsOffcanvas, setShowStatsOffcanvas] = useState(false);
  const [patientStats, setPatientStats] = useState(null);
  
  // Filter state
  const [filters, setFilters] = useState({
    status: 'All',
    type: '',
    doctor_id: '',
    center_id: '',
    performed_from: '',
    performed_to: '',
    min_amount: '',
    max_amount: ''
  });

  // Función principal para cargar actividades del paciente
  const loadPatientActivities = useCallback(async () => {
    if (!patientId) {
      console.log('No patientId provided, component will show empty state');
      setActivities([]);
      onCountUpdate && onCountUpdate(componentName, 0);
      return;
    }
    
    setIsLoading(true);
    try {
      console.log('Loading billing activities for patient:', patientId);
      
      const response = await PatientBillingService.getPatientActivities(patientId, {
        include_stats: true,
        page_size: 100, // Cargar todas las actividades
        sort: 'performed_at:-1'
      });
      
      console.log('Raw API response:', response);
      
      let activitiesData = [];
      
      if (response.data && response.data.activities) {
        activitiesData = response.data.activities;
        
        // Guardar estadísticas si están disponibles
        if (response.data.stats) {
          setPatientStats(response.data.stats);
        }
      } else if (response.activities) {
        activitiesData = response.activities;
      }

      const normalizedActivities = activitiesData.map(activity => {
        console.log('Processing billing activity:', activity);
        
        return {
          // Primary identifiers
          activity_id: activity._id || activity.id,
          
          // Activity information
          code: activity.code || '',
          description: activity.description || '',
          type: activity.type || '',
          performed_at: activity.performed_at || activity.created_at,
          status: activity.status || 'pending',
          
          // Pricing information
          pricing: activity.pricing || {},
          amounts: activity.amounts || { net: 0, gross: 0, tax: 0 },
          split: activity.split || [],
          
          // Personnel info
          doctor_name: activity.doctor_name || activity.doctor_info?.full_name || 'Unknown',
          doctor_id: activity.doctor_id,
          center_name: activity.center_info?.name || 'Unknown Center',
          center_id: activity.center_id,
          
          // Patient info
          patient_id: activity.patient_id || patientId,
          patient_name: activity.patient_info?.full_name || patientData.patientName || 'Unknown Patient',
          
          // Display information
          display_info: activity.display_info || {},
          
          // Metadata
          created_at: activity.created_at,
          updated_at: activity.updated_at,
          
          // Sales Order links
          so_links: activity.so_links || [],
          
          // Preserve original for detailed view
          _original: activity
        };
      });

      console.log('Normalized activities:', normalizedActivities);
      
      // Actualizar contador solo si cambió
      if (activities.length !== normalizedActivities.length) {
        console.log(`📊 ${componentName}: Contador cambió de ${activities.length} a ${normalizedActivities.length}`);
        onCountUpdate && onCountUpdate(componentName, normalizedActivities.length);
      }
      
      setActivities(normalizedActivities);
    } catch (error) {
      console.error('Error loading billing activities:', error);
      setActivities([]);
      // Actualizar contador en caso de error
      if (activities.length !== 0) {
        onCountUpdate && onCountUpdate(componentName, 0);
      }
    } finally {
      setIsLoading(false);
    }
  }, [patientId, activities.length, onCountUpdate, componentName, patientData]);

  // Función para cargar alertas y estadísticas
  const loadActivitiesAlerts = useCallback(async () => {
    if (!patientId || activities.length === 0) return;
    
    try {
      const pendingCount = activities.filter(activity => 
        ['pending', 'ready'].includes(activity.status)
      ).length;
      
      const pendingAmount = activities
        .filter(activity => ['pending', 'ready'].includes(activity.status))
        .reduce((sum, activity) => sum + (activity.amounts.net || 0), 0);
      
      if (pendingCount > 0) {
        setAlertInfo({
          alert_type: 'billing',
          message: `${pendingCount} pending billing activities (${BillingUtils.formatCurrency(pendingAmount)})`,
          priority: pendingAmount > 500 ? 'high' : 'medium',
          requires_action: pendingAmount > 1000
        });
      } else {
        setAlertInfo(null);
      }
    } catch (error) {
      console.error('Error loading billing alerts:', error);
      setAlertInfo(null);
    }
  }, [patientId, activities]);

  const applyFilters = useCallback(() => {
    console.log('🔄 Aplicando filtros y ordenamiento...');
    console.log('Activities array length:', activities.length);
    console.log('Current sortBy:', sortBy);
    
    let filtered = [...activities];

    // Aplicar filtro de búsqueda global
    if (globalFilter) {
      filtered = filtered.filter(activity => 
        (activity.code || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
        (activity.description || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
        (activity.doctor_name || '').toLowerCase().includes(globalFilter.toLowerCase()) ||
        (activity.type || '').toLowerCase().includes(globalFilter.toLowerCase())
      );
    }

    // Aplicar filtros específicos
    if (filters.status !== 'All') {
      filtered = filtered.filter(activity => activity.status === filters.status.toLowerCase());
    }

    if (filters.type) {
      filtered = filtered.filter(activity => 
        (activity.type || '').toLowerCase() === filters.type.toLowerCase()
      );
    }

    if (filters.doctor_id) {
      filtered = filtered.filter(activity => activity.doctor_id === filters.doctor_id);
    }

    if (filters.center_id) {
      filtered = filtered.filter(activity => activity.center_id === filters.center_id);
    }

    if (filters.performed_from) {
      filtered = filtered.filter(activity => {
        const performedDate = activity.performed_at;
        return performedDate && new Date(performedDate) >= new Date(filters.performed_from);
      });
    }

    if (filters.performed_to) {
      filtered = filtered.filter(activity => {
        const performedDate = activity.performed_at;
        return performedDate && new Date(performedDate) <= new Date(filters.performed_to);
      });
    }

    if (filters.min_amount) {
      filtered = filtered.filter(activity => 
        (activity.amounts?.net || 0) >= parseFloat(filters.min_amount)
      );
    }

    if (filters.max_amount) {
      filtered = filtered.filter(activity => 
        (activity.amounts?.net || 0) <= parseFloat(filters.max_amount)
      );
    }

    console.log('Filtered array length:', filtered.length);

    // Ordenamiento
    filtered.sort((a, b) => {
      console.log('Sorting with:', sortBy);
      
      const getDate = (item) => {
        const dateValue = item.performed_at || item.created_at || new Date().toISOString();
        return new Date(dateValue);
      };
      
      switch (sortBy) {
        case 'newest':
          return getDate(b) - getDate(a);
        case 'oldest':
          return getDate(a) - getDate(b);
        case 'amount_desc': {
          const amountA = a.amounts?.net || 0;
          const amountB = b.amounts?.net || 0;
          return amountB - amountA;
        }
        case 'amount_asc': {
          const amountA = a.amounts?.net || 0;
          const amountB = b.amounts?.net || 0;
          return amountA - amountB;
        }
        case 'status': {
          const statusOrder = { 'pending': 1, 'ready': 2, 'so_created': 3, 'partially_invoiced': 4, 'fully_invoiced': 5 };
          return (statusOrder[a.status] || 0) - (statusOrder[b.status] || 0);
        }
        case 'alphabetical': {
          const descA = (a.description || '').toLowerCase();
          const descB = (b.description || '').toLowerCase();
          return descA.localeCompare(descB);
        }
        default:
          return 0;
      }
    });

    console.log('Final filtered array length:', filtered.length);
    setFilteredActivities(filtered);
  }, [activities, filters, globalFilter, sortBy]);

  const handleGlobalFilterChange = (filter) => {
    setGlobalFilter(filter);
  };
const handleAddNew = () => {
  setShowNewOrderModal(true);
};

const handleNewOrderClose = () => {
  setShowNewOrderModal(false);
};

const handleNewOrderSave = (savedData) => {
  console.log('New activities saved:', savedData);
  // Recargar las actividades del paciente
  if (patientId) {
    loadPatientActivities();
  }
  setShowNewOrderModal(false);
};

  const handleSortChange = useCallback((sortOption) => {
    console.log('Cambiando orden a:', sortOption);
    setSortBy(sortOption);
    setShowSortDropdown(false);
  }, []);

  const getSortDisplayText = () => {
    switch (sortBy) {
      case 'newest': return 'Newest First';
      case 'oldest': return 'Oldest First';
      case 'amount_desc': return 'Amount (High to Low)';
      case 'amount_asc': return 'Amount (Low to High)';
      case 'status': return 'By Status';
      case 'alphabetical': return 'A-Z';
      default: return 'Sort by';
    }
  };

  // Table columns definition
  const columns = useMemo(() => [
    {
      accessorKey: 'description',
      header: 'Activity',
      size: 250,
      Cell: ({ row }) => (
        <div className="d-flex align-items-center">
          <div className="me-2">
            {getActivityTypeIcon(row.original.type)}
          </div>
          <div>
            <div className="fw-bold text-primary">
              {row.original.description || 'Unknown Activity'}
            </div>
            <div className="d-flex align-items-center gap-2">
              <small className="text-muted">
                {row.original.code || 'No code'}
              </small>
              <small className="text-muted">
                • {BillingUtils.formatDate(row.original.performed_at)}
              </small>
            </div>
          </div>
        </div>
      )
    },
    {
      accessorKey: 'type',
      header: 'Type',
      size: 120,
      Cell: ({ row }) => (
        <span className={`badge bg-${getActivityTypeColor(row.original.type)}`}>
          {getActivityTypeLabel(row.original.type)}
        </span>
      )
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 140,
      Cell: ({ row }) => (
        <div className="d-flex align-items-center gap-1">
          {getStatusIcon(row.original.status)}
          <span className={`badge bg-${getStatusColor(row.original.status)}`}>
            {getStatusLabel(row.original.status)}
          </span>
        </div>
      )
    },
    {
      accessorKey: 'amounts',
      header: 'Amount',
      size: 120,
      Cell: ({ row }) => (
        <div>
          <div className="fw-bold">
            {BillingUtils.formatCurrency(row.original.amounts?.net || 0)}
          </div>
          {row.original.amounts?.tax > 0 && (
            <small className="text-muted">
              +{BillingUtils.formatCurrency(row.original.amounts.tax)} tax
            </small>
          )}
        </div>
      )
    },
    {
      accessorKey: 'doctor_name',
      header: 'Provider',
      size: 150,
      Cell: ({ row }) => (
        <div>
          <div>{row.original.doctor_name || 'Unknown'}</div>
          <small className="text-muted">
            {row.original.center_name || 'Unknown Center'}
          </small>
        </div>
      )
    },
    {
      accessorKey: 'split',
      header: 'Payers',
      size: 150,
      Cell: ({ row }) => {
        const payers = row.original.split || [];
        if (payers.length === 0) {
          return <span className="text-muted">No payers</span>;
        }
        
        return (
          <div>
            {payers.slice(0, 2).map((payer, index) => (
              <div key={index} className="d-flex align-items-center gap-1">
                <small className="text-truncate" style={{maxWidth: '80px'}}>
                  {payer.payer_snapshot?.name || 'Unknown'}
                </small>
                <small className="text-muted">({payer.percent}%)</small>
              </div>
            ))}
            {payers.length > 2 && (
              <small className="text-muted">+{payers.length - 2} more</small>
            )}
          </div>
        );
      }
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 120,
      Cell: ({ row }) => (
        <div className="d-flex gap-1">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              handleViewActivity(row.original.activity_id);
            }}
            title="View Details"
          >
            <FiEye />
          </button>
          {canCreateInvoice(row.original) && (
            <button
              className="btn btn-outline-success btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                handleCreateInvoiceForActivity(row.original.activity_id);
              }}
              title="Create Invoice"
            >
              <FiFileText />
            </button>
          )}
        </div>
      )
    }
  ], [activities]);

  // Effects
  useEffect(() => {
    console.log(`🔄 ${componentName}: useEffect triggered, patientId:`, patientId);
    if (patientId) {
      loadPatientActivities();
    }
  }, [patientId, loadPatientActivities]);

  useEffect(() => {
    if (activities.length > 0) {
      loadActivitiesAlerts();
    }
  }, [activities, loadActivitiesAlerts]);

  useEffect(() => {
    console.log(`📊 ${componentName}: Actualizando contador a ${activities.length}`);
    onCountUpdate && onCountUpdate(componentName, activities.length);
  }, [activities.length, onCountUpdate, componentName]);

  useEffect(() => {
    console.log('📊 useEffect applyFilters triggered');
    console.log('- activities.length:', activities.length);
    console.log('- sortBy:', sortBy);
    
    if (activities.length > 0) {
      applyFilters();
    } else {
      console.log('⚠️ No hay activities, limpiando filteredActivities');
      setFilteredActivities([]);
    }
  }, [activities, filters, globalFilter, sortBy, applyFilters]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown')) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Prevent body scroll when offcanvas are open
  useEffect(() => {
    if (showFilterOffcanvas || showViewOffcanvas || showCreateInvoiceModal || showStatsOffcanvas) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '17px';
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0px';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.paddingRight = '0px';
    };
  }, [showFilterOffcanvas, showViewOffcanvas, showCreateInvoiceModal, showStatsOffcanvas]);

  // Loading state
  if (isLoading && patientId) {
    return <div>Loading billing data...</div>;
  }

  // Event handlers
  const handleViewActivity = (activityId) => {
    console.log('Viewing activity:', activityId);
    const activity = activities.find(a => a.activity_id === activityId);
    if (activity) {
      console.log('Found activity for viewing:', activity);
      setSelectedActivity(activity);
      setShowViewOffcanvas(true);
    } else {
      console.error('Activity not found:', activityId);
    }
  };

  const handleCreateInvoiceForActivity = (activityId) => {
    console.log('Creating invoice for activity:', activityId);
    setRowSelection({ [activityId]: true });
    setShowCreateInvoiceModal(true);
  };

  const handleBulkCreateInvoice = () => {
    const selectedIds = Object.keys(rowSelection).filter(id => rowSelection[id]);
    if (selectedIds.length === 0) {
      alert('Please select at least one activity to create invoice');
      return;
    }
    setShowCreateInvoiceModal(true);
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const handleApplyFilters = () => {
    setShowFilterOffcanvas(false);
  };

  const handleClearFilters = () => {
    setFilters({
      status: 'All',
      type: '',
      doctor_id: '',
      center_id: '',
      performed_from: '',
      performed_to: '',
      min_amount: '',
      max_amount: ''
    });
  };

  // Utility functions
  const getActivityTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'consultation': return <FiUser className="text-primary" />;
      case 'procedure': return <FiCreditCard className="text-success" />;
      case 'test': return <FiFileText className="text-info" />;
      case 'material': return <FiDollarSign className="text-warning" />;
      default: return <FiInfo className="text-secondary" />;
    }
  };

  const getActivityTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'consultation': return 'primary';
      case 'procedure': return 'success';
      case 'test': return 'info';
      case 'material': return 'warning';
      default: return 'secondary';
    }
  };

  const getActivityTypeLabel = (type) => {
    switch (type?.toLowerCase()) {
      case 'consultation': return 'Consultation';
      case 'procedure': return 'Procedure';
      case 'test': return 'Test';
      case 'material': return 'Material';
      default: return type || 'Unknown';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return <FiClock className="text-warning" />;
      case 'ready': return <FiCheck className="text-primary" />;
      case 'so_created': return <FiFileText className="text-info" />;
      case 'partially_invoiced': return <FiAlertTriangle className="text-warning" />;
      case 'fully_invoiced': return <FiCheckCircle className="text-success" />;
      case 'non_billable': return <FiXCircle className="text-danger" />;
      default: return <FiInfo className="text-secondary" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'warning';
      case 'ready': return 'primary';
      case 'so_created': return 'info';
      case 'partially_invoiced': return 'warning';
      case 'fully_invoiced': return 'success';
      case 'non_billable': return 'danger';
      default: return 'secondary';
    }
  };

  const getStatusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'Pending';
      case 'ready': return 'Ready';
      case 'so_created': return 'Order Created';
      case 'partially_invoiced': return 'Partial Invoice';
      case 'fully_invoiced': return 'Fully Invoiced';
      case 'non_billable': return 'Non-billable';
      default: return status || 'Unknown';
    }
  };

  const canCreateInvoice = (activity) => {
    return ['pending', 'ready'].includes(activity.status?.toLowerCase());
  };

  const getActiveFilters = () => {
    const activeFilters = [];
    
    if (globalFilter) {
      activeFilters.push({
        key: 'search',
        label: `Search: "${globalFilter}"`,
        value: globalFilter,
        type: 'search'
      });
    }
    
    if (filters.status !== 'All') {
      activeFilters.push({
        key: 'status',
        label: `Status: ${filters.status}`,
        value: filters.status,
        type: 'status'
      });
    }
    
    if (filters.type) {
      activeFilters.push({
        key: 'type',
        label: `Type: ${filters.type}`,
        value: filters.type,
        type: 'type'
      });
    }
    
    if (filters.performed_from) {
      activeFilters.push({
        key: 'performed_from',
        label: `From: ${BillingUtils.formatDate(filters.performed_from)}`,
        value: filters.performed_from,
        type: 'performed_from'
      });
    }
    
    if (filters.performed_to) {
      activeFilters.push({
        key: 'performed_to',
        label: `To: ${BillingUtils.formatDate(filters.performed_to)}`,
        value: filters.performed_to,
        type: 'performed_to'
      });
    }
    
    if (sortBy !== 'newest') {
      activeFilters.push({
        key: 'sort',
        label: `Sort: ${getSortDisplayText()}`,
        value: sortBy,
        type: 'sort'
      });
    }
    
    return activeFilters;
  };

  const handleRemoveFilter = (filterToRemove) => {
    switch (filterToRemove.type) {
      case 'search':
        setGlobalFilter('');
        break;
      case 'status':
        setFilters(prev => ({ ...prev, status: 'All' }));
        break;
      case 'type':
        setFilters(prev => ({ ...prev, type: '' }));
        break;
      case 'performed_from':
        setFilters(prev => ({ ...prev, performed_from: '' }));
        break;
      case 'performed_to':
        setFilters(prev => ({ ...prev, performed_to: '' }));
        break;
      case 'sort':
        setSortBy('newest');
        break;
      default:
        break;
    }
  };

  const handleClearAllActiveFilters = () => {
    setGlobalFilter('');
    setFilters({
      status: 'All',
      type: '',
      doctor_id: '',
      center_id: '',
      performed_from: '',
      performed_to: '',
      min_amount: '',
      max_amount: ''
    });
    setSortBy('newest');
  };

  return (
    <main id="app">
      <TopHeader />
      <div className="container-fluid w-100">
        <PatientManagement activeMenu='/orders-treatment'/>
        
        {/* Breadcrumb */}
        <div className="mb-3">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><a href="/dashboard">Dashboard</a></li>
              <li className="breadcrumb-item"><a href="/patients/list">Patients</a></li>
              <li className="breadcrumb-item active" aria-current="page">
                My Orders {patientData?.patientName ? `- ${patientData.patientName}` : ''}
              </li>
            </ol>
          </nav>
        </div>

        <div className="card-body">
          {/* Enhanced Search and Actions Bar */}
          <div className="row mb-3">
            <div className="col-12">
              <div className="d-flex align-items-center justify-content-between">
                
                {/* Left side - Search */}
                <div className="d-flex align-items-center gap-2">
                  <div className="input-group" style={{maxWidth: '300px'}}>
                    <span className="input-group-text">
                      <FiSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search activities"
                      value={globalFilter}
                      onChange={(e) => handleGlobalFilterChange(e.target.value)}
                    />
                  </div>
                </div>

                {/* Right side - Controls */}
                <div className="d-flex align-items-center gap-2">
                  {/* Sort By Dropdown */}
                  <div className="dropdown" style={{position: 'relative'}}>
                    <button
                      className="btn btn-outline-secondary d-flex align-items-center gap-2"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSortDropdown(!showSortDropdown);
                      }}
                    >
                      {getSortDisplayText()}
                      <FiChevronDown />
                    </button>
                    {showSortDropdown && (
                      <div className="dropdown-menu show" style={{position: 'absolute', top: '100%', right: 0, zIndex: 1000}}>
                        <button
                          className={`dropdown-item ${sortBy === 'newest' ? 'active' : ''}`}
                          onClick={() => handleSortChange('newest')}
                        >
                          Newest First
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'oldest' ? 'active' : ''}`}
                          onClick={() => handleSortChange('oldest')}
                        >
                          Oldest First
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'amount_desc' ? 'active' : ''}`}
                          onClick={() => handleSortChange('amount_desc')}
                        >
                          Amount (High to Low)
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'amount_asc' ? 'active' : ''}`}
                          onClick={() => handleSortChange('amount_asc')}
                        >
                          Amount (Low to High)
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'status' ? 'active' : ''}`}
                          onClick={() => handleSortChange('status')}
                        >
                          By Status
                        </button>
                        <button
                          className={`dropdown-item ${sortBy === 'alphabetical' ? 'active' : ''}`}
                          onClick={() => handleSortChange('alphabetical')}
                        >
                          A-Z
                        </button>
                      </div>
                      )}
                  </div>

                  {/* Advanced Filter Button */}
                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={() => setShowFilterOffcanvas(true)}
                  >
                    <FiFilter />
                    Filter
                  </button>

                  {/* Add New Button */}
                                    <button
                                      className="btn btn-primary d-flex align-items-center gap-2"
                                      onClick={handleAddNew}
                                    >
                                      <FiPlus />
                                      Add New
                                    </button>

                  {/* Patient Stats Button */}
                  {/* {patientStats && (
                    <button
                      className="btn btn-outline-info d-flex align-items-center gap-2"
                      onClick={() => setShowStatsOffcanvas(true)}
                    >
                      <FiInfo />
                      Stats
                    </button>
                  )} */}

                  {/* Bulk Actions */}
                  {Object.keys(rowSelection).filter(id => rowSelection[id]).length > 0 && (
                    <button
                      className="btn btn-success d-flex align-items-center gap-2"
                      onClick={handleBulkCreateInvoice}
                    >
                      <FiFileText />
                      Create Invoice ({Object.keys(rowSelection).filter(id => rowSelection[id]).length})
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Active Filters */}
          {getActiveFilters().length > 0 && (
            <div className="row mb-3">
              <div className="col-12">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <small className="text-muted me-2">Active filters:</small>
                  {getActiveFilters().map((filter, index) => (
                    <span
                      key={index}
                      className="badge bg-light text-dark d-flex align-items-center gap-1"
                      style={{ cursor: 'pointer' }}
                    >
                      {filter.label}
                      <FiX 
                        size={12}
                        onClick={() => handleRemoveFilter(filter)}
                        className="text-danger"
                      />
                    </span>
                  ))}
                  <button
                    className="btn btn-link btn-sm text-decoration-none p-0"
                    onClick={handleClearAllActiveFilters}
                  >
                    Clear all
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Alert Info */}
          {/* {alertInfo && (
            <div className={`alert alert-${alertInfo.priority === 'high' ? 'warning' : 'info'} alert-dismissible fade show`}>
              <div className="d-flex align-items-center gap-2">
                <FiAlertTriangle />
                <span>{alertInfo.message}</span>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={() => setAlertInfo(null)}
              ></button>
            </div>
          )} */}

          {/* Patient Medical Clinical Data Component */}
          <div className="row">
            <div className="col-12">
              <PatientMedicalClinicalData patientData={patientData} />
            </div>
          </div>

          {/* Main Table */}
          <div className="row ">
            <div className="col-12">
              {!patientId ? (
                <div className="text-center py-5">
                  <FiUser size={48} className="text-muted mb-3" />
                  <h5 className="text-muted">No Patient Selected</h5>
                  <p className="text-muted">Please select a patient to view billing activities.</p>
                </div>
              ) : filteredActivities.length === 0 && !isLoading ? (
                <div className="text-center py-5">
                  <FiFileText size={48} className="text-muted mb-3" />
                  <h5 className="text-muted">No Billing Activities Found</h5>
                  <p className="text-muted">
                    {activities.length === 0 
                      ? 'This patient has no billing activities yet.'
                      : 'No activities match your current filters.'
                    }
                  </p>
                  {getActiveFilters().length > 0 && (
                    <button
                      className="btn btn-outline-primary"
                      onClick={handleClearAllActiveFilters}
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              ) : (
                <MaterialReactTable
                  columns={columns}
                  data={filteredActivities}
                  state={{
                    isLoading: isLoading,
                    rowSelection: rowSelection,
                    globalFilter: globalFilter
                  }}
                  onGlobalFilterChange={handleGlobalFilterChange}
                  onRowSelectionChange={setRowSelection}
                  enableRowSelection={true}
                  enableGlobalFilter={true} // We handle this manually
                  enableColumnFilters={true}
                  enablePagination={true}
                  enableSorting={false} // We handle sorting manually
                  initialState={{
                    pagination: { pageSize: 10, pageIndex: 0 }
                  }}
                  muiTableContainerProps={{
                    sx: {
                      maxHeight: '600px'
                    }
                  }}
                  renderTopToolbar={() => null}
                  renderBottomToolbar={({ table }) => (
                    <div className="d-flex justify-content-between align-items-center p-3">
                      <div className="text-muted">
                        Showing {table.getRowModel().rows.length} of {filteredActivities.length} activities
                        {activities.length !== filteredActivities.length && 
                          ` (filtered from ${activities.length} total)`
                        }
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => table.previousPage()}
                          disabled={!table.getCanPreviousPage()}
                        >
                          Previous
                        </button>
                        <span className="align-self-center">
                          Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
                        </span>
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => table.nextPage()}
                          disabled={!table.getCanNextPage()}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Filters Offcanvas */}
      {showFilterOffcanvas && (
        <div className="offcanvas offcanvas-end show" style={{visibility: 'visible'}} tabIndex="-1">
          <div className="offcanvas-header">
            <h5 className="offcanvas-title">Advanced Filters</h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => setShowFilterOffcanvas(false)}
            ></button>
          </div>
          <div className="offcanvas-body">
            <form>
              {/* Status Filter */}
              <div className="mb-3">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="All">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="ready">Ready</option>
                  <option value="so_created">Order Created</option>
                  <option value="partially_invoiced">Partially Invoiced</option>
                  <option value="fully_invoiced">Fully Invoiced</option>
                  <option value="non_billable">Non-billable</option>
                </select>
              </div>

              {/* Type Filter */}
              <div className="mb-3">
                <label className="form-label">Activity Type</label>
                <select
                  className="form-select"
                  value={filters.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                >
                  <option value="">All Types</option>
                  <option value="consultation">Consultation</option>
                  <option value="procedure">Procedure</option>
                  <option value="test">Test</option>
                  <option value="material">Material</option>
                </select>
              </div>

              {/* Date Range */}
              <div className="mb-3">
                <label className="form-label">Date Range</label>
                <div className="row">
                  <div className="col-6">
                    <input
                      type="date"
                      className="form-control"
                      placeholder="From"
                      value={filters.performed_from}
                      onChange={(e) => handleFilterChange('performed_from', e.target.value)}
                    />
                  </div>
                  <div className="col-6">
                    <input
                      type="date"
                      className="form-control"
                      placeholder="To"
                      value={filters.performed_to}
                      onChange={(e) => handleFilterChange('performed_to', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Amount Range */}
              <div className="mb-3">
                <label className="form-label">Amount Range</label>
                <div className="row">
                  <div className="col-6">
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Min Amount"
                      value={filters.min_amount}
                      onChange={(e) => handleFilterChange('min_amount', e.target.value)}
                    />
                  </div>
                  <div className="col-6">
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Max Amount"
                      value={filters.max_amount}
                      onChange={(e) => handleFilterChange('max_amount', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-primary flex-fill"
                  onClick={handleApplyFilters}
                >
                  Apply Filters
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleClearFilters}
                >
                  Clear
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Activity Offcanvas */}
      {showViewOffcanvas && selectedActivity && (
        <div className="offcanvas offcanvas-end show" style={{visibility: 'visible', width: '50%'}} tabIndex="-1">
          <div className="offcanvas-header">
            <h5 className="offcanvas-title">Activity Details</h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => setShowViewOffcanvas(false)}
            ></button>
          </div>
          <div className="offcanvas-body">
            {/* Activity Information */}
            <div className="card mb-3">
              <div className="card-header">
                <h6 className="mb-0">Activity Information</h6>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-sm-4"><strong>Code:</strong></div>
                  <div className="col-sm-8">{selectedActivity.code || 'N/A'}</div>
                </div>
                <div className="row">
                  <div className="col-sm-4"><strong>Description:</strong></div>
                  <div className="col-sm-8">{selectedActivity.description || 'N/A'}</div>
                </div>
                <div className="row">
                  <div className="col-sm-4"><strong>Type:</strong></div>
                  <div className="col-sm-8">
                    <span className={`badge bg-${getActivityTypeColor(selectedActivity.type)}`}>
                      {getActivityTypeLabel(selectedActivity.type)}
                    </span>
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-4"><strong>Status:</strong></div>
                  <div className="col-sm-8">
                    <span className={`badge bg-${getStatusColor(selectedActivity.status)}`}>
                      {getStatusLabel(selectedActivity.status)}
                    </span>
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-4"><strong>Performed:</strong></div>
                  <div className="col-sm-8">{BillingUtils.formatDate(selectedActivity.performed_at)}</div>
                </div>
              </div>
            </div>

            {/* Pricing Information */}
            <div className="card mb-3">
              <div className="card-header">
                <h6 className="mb-0">Pricing Information</h6>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-sm-4"><strong>Net Amount:</strong></div>
                  <div className="col-sm-8">{BillingUtils.formatCurrency(selectedActivity.amounts?.net || 0)}</div>
                </div>
                <div className="row">
                  <div className="col-sm-4"><strong>Tax:</strong></div>
                  <div className="col-sm-8">{BillingUtils.formatCurrency(selectedActivity.amounts?.tax || 0)}</div>
                </div>
                <div className="row">
                  <div className="col-sm-4"><strong>Gross Amount:</strong></div>
                  <div className="col-sm-8 fw-bold">{BillingUtils.formatCurrency(selectedActivity.amounts?.gross || 0)}</div>
                </div>
              </div>
            </div>

            {/* Provider Information */}
            <div className="card mb-3">
              <div className="card-header">
                <h6 className="mb-0">Provider Information</h6>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-sm-4"><strong>Doctor:</strong></div>
                  <div className="col-sm-8">{selectedActivity.doctor_name || 'Unknown'}</div>
                </div>
                <div className="row">
                  <div className="col-sm-4"><strong>Center:</strong></div>
                  <div className="col-sm-8">{selectedActivity.center_name || 'Unknown'}</div>
                </div>
              </div>
            </div>

            {/* Payer Split */}
            {selectedActivity.split && selectedActivity.split.length > 0 && (
              <div className="card mb-3">
                <div className="card-header">
                  <h6 className="mb-0">Payer Split</h6>
                </div>
                <div className="card-body">
                  {selectedActivity.split.map((payer, index) => (
                    <div key={index} className="d-flex justify-content-between align-items-center mb-2">
                      <span>{payer.payer_snapshot?.name || 'Unknown Payer'}</span>
                      <span className="badge bg-primary">{payer.percent}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="d-flex gap-2">
              {canCreateInvoice(selectedActivity) && (
                <button
                  className="btn btn-success"
                  onClick={() => {
                    setShowViewOffcanvas(false);
                    handleCreateInvoiceForActivity(selectedActivity.activity_id);
                  }}
                >
                  <FiFileText className="me-2" />
                  Create Invoice
                </button>
              )}
              <button
                className="btn btn-outline-secondary"
                onClick={() => setShowViewOffcanvas(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Patient Stats Offcanvas */}
      {/* {showStatsOffcanvas && patientStats && (
        <div className="offcanvas offcanvas-end show" style={{visibility: 'visible'}} tabIndex="-1">
          <div className="offcanvas-header">
            <h5 className="offcanvas-title">Patient Billing Statistics</h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => setShowStatsOffcanvas(false)}
            ></button>
          </div>
          <div className="offcanvas-body">
            <div className="row">
              <div className="col-12 mb-3">
                <div className="card">
                  <div className="card-body text-center">
                    <h5 className="card-title">Total Activities</h5>
                    <h2 className="text-primary">{patientStats.total_count || 0}</h2>
                  </div>
                </div>
              </div>
              <div className="col-12 mb-3">
                <div className="card">
                  <div className="card-body text-center">
                    <h5 className="card-title">Total Amount</h5>
                    <h2 className="text-success">{BillingUtils.formatCurrency(patientStats.total_amount || 0)}</h2>
                  </div>
                </div>
              </div>
              <div className="col-12 mb-3">
                <div className="card">
                  <div className="card-body text-center">
                    <h5 className="card-title">Pending Amount</h5>
                    <h2 className="text-warning">{BillingUtils.formatCurrency(patientStats.pending_amount || 0)}</h2>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )} */}

      {/* Create Invoice Modal */}
      {showCreateInvoiceModal && (
        <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create Invoice</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowCreateInvoiceModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p>Invoice creation functionality will be implemented here.</p>
                <p>Selected activities: {Object.keys(rowSelection).filter(id => rowSelection[id]).length}</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateInvoiceModal(false)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    // Implement invoice creation logic here
                    console.log('Creating invoice for activities:', Object.keys(rowSelection).filter(id => rowSelection[id]));
                    setShowCreateInvoiceModal(false);
                  }}
                >
                  Create Invoice
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* New Order Treatment Modal */}
<NewOrderTreatment
  show={showNewOrderModal}
  handleClose={handleNewOrderClose}
  onSave={handleNewOrderSave}
  patientData={patientData}
  currentCenterId={patientData?.center_id || null}
/>

      {/* Offcanvas Backdrop */}
      {(showFilterOffcanvas || showViewOffcanvas || showStatsOffcanvas) && (
        <div 
          className="offcanvas-backdrop fade show"
          onClick={() => {
            setShowFilterOffcanvas(false);
            setShowViewOffcanvas(false);
            setShowStatsOffcanvas(false);
          }}
        ></div>
      )}
      
    </main>
  );
};

export default OrdersTreatment;